import React, { useState, useEffect } from 'react';
import {
    Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert,
    Tooltip, IconButton, Switch, FormControlLabel
} from '@mui/material';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import api from '../services/api';
import { confirmAcao, toastSuccess } from '../services/alerts';
import { mostrarErro } from '../services/erros';
import { isVisualizador } from '../services/auth';

export default function Periodicidade() {
    const soLeitura = isVisualizador();
    const [periodicidades, setPeriodicidades] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPeriodicidade, setEditingPeriodicidade] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

    const initialState = {
        nome: '',
        intervaloDias: '',
        quantidadeSessoesBase: '',
        ativo: true
    };
    const [formData, setFormData] = useState(initialState);

    useEffect(() => {
        carregarPeriodicidades();
    }, []);

    const carregarPeriodicidades = async () => {
        setLoading(true);
        try {
            const response = await api.get('/periodicidades');
            const d = response.data;
            setPeriodicidades(Array.isArray(d) ? d : (d?.content ?? []));
        } catch (error) {
            console.error('Erro ao carregar periodicidades:', error);
            mostrarMensagem('Erro ao carregar dados.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (periodicidade = null) => {
        if (periodicidade) {
            setEditingPeriodicidade(periodicidade);
            setFormData({
                id_periodicidade: periodicidade.id_periodicidade,
                nome: periodicidade.nome || '',
                intervaloDias: periodicidade.intervaloDias || '',
                quantidadeSessoesBase: periodicidade.quantidadeSessoesBase || '',
                ativo: periodicidade.ativo !== undefined ? periodicidade.ativo : true
            });
        } else {
            setEditingPeriodicidade(null);
            setFormData(initialState);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingPeriodicidade(null);
    };

    const handleSave = async () => {
        if (!formData.nome) {
            mostrarMensagem('Preencha o campo Nome.', 'warning');
            return;
        }

        try {
            if (editingPeriodicidade) {
                await api.put(`/periodicidades/${editingPeriodicidade.id_periodicidade}`, formData, { skipGlobalError: true });
                toastSuccess('Periodicidade atualizada.');
            } else {
                await api.post('/periodicidades', formData, { skipGlobalError: true });
                toastSuccess('Periodicidade criada.');
            }
            carregarPeriodicidades();
            handleCloseDialog();
        } catch (error) {
            mostrarErro(error, 'salvar a periodicidade');
        }
    };

    const handleDelete = async (id) => {
        const ok = await confirmAcao({
            title: 'Excluir periodicidade?',
            text: 'Esta ação não pode ser desfeita.',
            confirmButtonText: 'Excluir',
            icon: 'warning',
            danger: true,
        });
        if (!ok) return;

        try {
            await api.delete(`/periodicidades/${id}`, { skipGlobalError: true });
            toastSuccess('Periodicidade excluída.');
            carregarPeriodicidades();
        } catch (error) {
            mostrarErro(error, 'excluir a periodicidade');
        }
    };

    const mostrarMensagem = (message, severity = 'success') => setSnackbar({ open: true, message, severity });

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold' }}>
                    Periodicidades
                </Typography>
                {!soLeitura && (
                    <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog()}>
                        Nova Periodicidade
                    </Button>
                )}
            </Box>

            <Paper elevation={0} sx={{ p: 3, borderRadius: 2 }}>
                <TableContainer>
                    <Table>
                        <TableHead>
                            <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                                <TableCell><strong>Nome</strong></TableCell>
                                <TableCell><strong>Intervalo (Dias)</strong></TableCell>
                                <TableCell><strong>Sessões Base</strong></TableCell>
                                <TableCell><strong>Status</strong></TableCell>
                                <TableCell align="right"><strong>Ações</strong></TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {!loading && periodicidades.length > 0 ? periodicidades.map((item) => (
                                <TableRow key={item.id_periodicidade} hover>
                                    <TableCell sx={{ fontWeight: 500 }}>{item.nome}</TableCell>
                                    <TableCell>{item.intervaloDias}</TableCell>
                                    <TableCell>{item.quantidadeSessoesBase}</TableCell>
                                    <TableCell>{item.ativo ? 'Ativo' : 'Inativo'}</TableCell>
                                    <TableCell align="right">
                                        {!soLeitura ? (
                                            <>
                                                <Tooltip title="Editar">
                                                    <IconButton onClick={() => handleOpenDialog(item)} size="small" color="primary"><EditIcon /></IconButton>
                                                </Tooltip>
                                                <Tooltip title="Excluir">
                                                    <IconButton onClick={() => handleDelete(item.id_periodicidade)} size="small" color="error"><DeleteIcon /></IconButton>
                                                </Tooltip>
                                            </>
                                        ) : (
                                            <Tooltip title="Visualizar">
                                                <IconButton onClick={() => handleOpenDialog(item)} size="small"><EditIcon /></IconButton>
                                            </Tooltip>
                                        )}
                                    </TableCell>
                                </TableRow>
                            )) : (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">{loading ? 'Carregando...' : 'Nenhuma periodicidade encontrada.'}</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold' }}>
                    {soLeitura ? 'Visualizar Periodicidade' : editingPeriodicidade ? 'Editar Periodicidade' : 'Nova Periodicidade'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 3, pt: 1 }}>
                        <Box sx={{ gridColumn: 'span 12' }}>
                            <TextField fullWidth required label="Nome" name="nome" value={formData.nome} onChange={(e) => setFormData({ ...formData, nome: e.target.value })} disabled={soLeitura} />
                        </Box>
                        <Box sx={{ gridColumn: 'span 6' }}>
                            <TextField fullWidth type="number" label="Intervalo em Dias" name="intervaloDias" value={formData.intervaloDias} onChange={(e) => setFormData({ ...formData, intervaloDias: e.target.value })} disabled={soLeitura} />
                        </Box>
                        <Box sx={{ gridColumn: 'span 6' }}>
                            <TextField fullWidth type="number" label="Quantidade Sessões Base" name="quantidadeSessoesBase" value={formData.quantidadeSessoesBase} onChange={(e) => setFormData({ ...formData, quantidadeSessoesBase: e.target.value })} disabled={soLeitura} />
                        </Box>
                        <Box sx={{ gridColumn: 'span 12' }}>
                             <FormControlLabel control={<Switch checked={formData.ativo} onChange={(e) => setFormData({...formData, ativo: e.target.checked})} disabled={soLeitura} />} label="Ativo" />
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button onClick={handleCloseDialog}>{soLeitura ? 'Fechar' : 'Cancelar'}</Button>
                    {!soLeitura && (
                        <Button onClick={handleSave} variant="contained">
                            {editingPeriodicidade ? 'Salvar Alterações' : 'Criar Periodicidade'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
                <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
}